import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  Input,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AsyncPipe, NgIf, NgTemplateOutlet } from '@angular/common';
import { ScreenDetectorService } from '@al00x/screen-detector';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MainLayoutService } from './main-layout.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { merge, take, tap } from 'rxjs';
import { MatMenuModule } from '@angular/material/menu';
import {
  CacButtonComponent,
  CacIconComponent,
  CacLoaderScreenComponent,
  RouteHelperService,
  RouteItem, AuthBaseStore, CacInitializeProtectionComponent
} from '@controladad/ng-base';
import { MatBadgeModule } from '@angular/material/badge';
import { AppStore } from '@core';

@Component({
  selector: 'lib-main-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    CacButtonComponent,
    CacIconComponent,
    NgTemplateOutlet,
    NgIf,
    AsyncPipe,
    MatSidenavModule,
    MatTabsModule,
    MatIconModule,
    MatRippleModule,
    MatProgressSpinnerModule,
    CacLoaderScreenComponent,
    MatButtonToggleModule,
    MatProgressBarModule,
    MatMenuModule,
    MatBadgeModule,
    CacInitializeProtectionComponent,
  ],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLayoutComponent {
  readonly injector = inject(Injector);
  readonly destroyRef = inject(DestroyRef);
  readonly layout = inject(MainLayoutService);
  readonly screenDetector = inject(ScreenDetectorService);
  readonly auth = inject(AuthBaseStore);
  readonly routeHelper = inject(RouteHelperService);
  readonly cdr = inject(ChangeDetectorRef);
  readonly app = inject(AppStore);

  @ViewChild('parent') sidebarEl!: MatSidenav;
  @ViewChild('children') sidenavEl!: MatSidenav;

  @Input() logoutStyle: 'icon' | 'text' = 'text';

  sidebarOpened = signal(false);
  sidebarShrink = signal<boolean>(this.app.state.shrinkSidebar ?? false);
  sidebarWidth = computed(() => (this.sidebarShrink() ? '64px' : '140px'));

  sidenavOpenedByPath = signal<string | undefined>(undefined);

  sidenavOpened = computed(() => this.sidenavOpenedByPath !== undefined);
  sidenavItems = computed(() => {
    const parent = this.routeHelper.getRouteItemAtLevel(0, this.sidenavOpenedByPath());
    if (!parent || !parent.children?.length) return undefined;
    const singletons: RouteItem[] = [];
    const grouped: RouteItem[] = [];
    for (const item of parent.children) {
      if (item.isPlaceholder) {
        grouped.push(item);
      } else {
        singletons.push(item);
      }
    }
    return {
      parent: parent,
      singletons,
      grouped,
    };
  });

  profileUrl = signal('/assets/base/images/profile.png');
  userInfo: WritableSignal<string | null> = signal('');
  isSuperAdmin = signal<boolean>(false);

  constructor() {
    merge(toObservable(this.layout.loading), toObservable(this.layout.softLoading))
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.cdr.detectChanges();
      });

    this.auth.state$
      .pipe(
        tap((state) => {
          this.userInfo.set(state?.user?.fullName || [state?.user?.firstName, state?.user?.lastName].filter(Boolean).join(' '));
          this.isSuperAdmin.set(this.auth.isSuper());
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  onLogout() {
    dialog$
      .prompt({
        title: '',
        message: 'Are you sure you want to logout?',
        yesButtonText: 'Logout',
        noButtonText: 'Cancel',
      })
      .afterSubmit()
      .pipe(take(1))
      .subscribe(() => {
        this.auth.logout();
      });
  }

  openSidenav(url?: string) {
    clearTimeout(this._setSidenavUndefinedTimeOut);
    this.sidenavEl.open();
    this.sidenavOpenedByPath.set(url ?? this.routeHelper.path());
  }

  private _setSidenavUndefinedTimeOut: any;
  closeSidenav() {
    this.sidenavEl.close();

    // delay is added to prevent content disappearing from sidenav when closing
    this._setSidenavUndefinedTimeOut = setTimeout(() => {
      this.sidenavOpenedByPath.set(undefined);
    }, 300);
  }

  toggleSidenav(url?: string) {
    if (this.sidenavOpened()) {
      this.closeSidenav();
    } else {
      this.openSidenav(url);
    }
  }

  openSidebar() {
    this.sidebarOpened.set(true);
  }
  closeSidebar() {
    this.sidebarOpened.set(false);
  }
  toggleSidebar() {
    if (this.sidebarOpened()) {
      this.closeSidebar();
    } else {
      this.openSidebar();
    }
  }

  toggleShrink() {
    this.sidebarShrink.set(!this.sidebarShrink());
    this.app.patch({
      shrinkSidebar: this.sidebarShrink(),
    });
  }

  navigate(item: RouteItem, isActive?: boolean) {
    if (item.isPlaceholder) {
      if (this.sidenavOpenedByPath() === item.path) {
        this.closeSidenav();
      } else {
        this.openSidenav(item.path);
      }
      return;
    }

    this.closeSidenav();
    if (!this.screenDetector.isDesktop$.value) this.closeSidebar();
    if (isActive) return;
    this.routeHelper.navigate(item);
  }

  navigateToSettings() {
    this.routeHelper.navigate('account-settings');
  }
}
